import { useState, useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Stethoscope,
  Plus,
  Search,
  Trash2,
  ChevronUp,
  ChevronDown,
  RotateCcw,
  Save,
  ChevronRight,
  FileText,
  Box,
  Sparkles,
  MapPin,
  Layers,
  X,
  ClipboardList,
  GitCompare,
  Target,
  Wrench,
  ScanLine,
  CheckCircle2,
  MessageSquare,
} from "lucide-react";
import { StructurePanel } from "./StructurePanel";
import { CreateCustomSectionDialog } from "./CreateCustomSectionDialog";
import { CreateCustomStructureDialog } from "./CreateCustomStructureDialog";
import type { TemplateSection, SectionConfig } from "@/hooks/use-admin-templates";
import type { AssignedStructure, BodyRegionStructure } from "@/hooks/use-body-region-structures";
import type { CreateCustomSectionInput, CreateCustomStructureInput } from "@/hooks/use-custom-sections-structures";
import type { CombinedSectionItem } from "@/hooks/use-combined-section-pool";

// Standard radiology report sections for quick-add
const STANDARD_SECTIONS = [
  { token: "EXAM", title: "Exam", icon: ScanLine },
  { token: "INDICATION", title: "Indication", icon: Target },
  { token: "COMPARISON", title: "Comparison", icon: GitCompare },
  { token: "TECHNIQUE", title: "Technique", icon: Wrench },
  { token: "FINDINGS", title: "Findings", icon: ClipboardList },
  { token: "IMPRESSION", title: "Impression", icon: CheckCircle2 },
  { token: "RECOMMENDATION", title: "Recommendation", icon: MessageSquare },
] as const;

export interface SectionWorkspaceProps {
  mode: "admin" | "user";
  sections: TemplateSection[];
  enabledTokens: Set<string>;
  availablePoolSections: CombinedSectionItem[];
  poolLoading: boolean;
  updating: boolean;

  // Admin CRUD on template_section_registry
  onUpdateSectionTitle?: (token: string, title: string) => Promise<boolean>;
  onUpdateDefaultText?: (token: string, text: string) => Promise<boolean>;
  onDeleteSection?: (token: string) => Promise<boolean>;
  onAddFromPool?: (section: CombinedSectionItem) => Promise<boolean>;
  onReorderSections?: (orderedTokens: string[]) => Promise<boolean>;

  // Toggle enabled/disabled (custom_sections filter)
  onToggleEnabled?: (token: string, enabled: boolean) => Promise<boolean>;
  onRestoreDefaults?: () => Promise<boolean>;

  // Whether custom_sections is set (non-null)
  isCustomized: boolean;

  // Structure management (optional - only needed if structures feature is enabled)
  assignedStructures?: AssignedStructure[];
  availableStructures?: BodyRegionStructure[];
  structureSearchResults?: BodyRegionStructure[];
  structuresLoading?: boolean;
  structureSearchLoading?: boolean;
  onToggleStructure?: (sectionToken: string, structureKey: string, include: boolean) => Promise<boolean>;
  onStructureSearch?: (query: string) => void;
  onClearStructureSearch?: () => void;

  // Custom section/structure creation
  examFamilyId?: string;
  onCreateCustomSection?: (input: CreateCustomSectionInput) => Promise<unknown>;
  onCreateCustomStructure?: (input: CreateCustomStructureInput) => Promise<unknown>;
}

export function SectionWorkspace({
  mode,
  sections,
  enabledTokens,
  availablePoolSections,
  poolLoading,
  updating,
  onUpdateSectionTitle,
  onUpdateDefaultText,
  onDeleteSection,
  onAddFromPool,
  onReorderSections,
  onToggleEnabled,
  onRestoreDefaults,
  isCustomized,
  // Structure props
  assignedStructures = [],
  availableStructures = [],
  structureSearchResults = [],
  structuresLoading = false,
  structureSearchLoading = false,
  onToggleStructure,
  onStructureSearch,
  onClearStructureSearch,
  // Custom creation props
  examFamilyId,
  onCreateCustomSection,
  onCreateCustomStructure,
}: SectionWorkspaceProps) {
  const isAdmin = mode === "admin";
  const hasStructureSupport = !!onToggleStructure;

  // Pool picker state
  const [poolSearch, setPoolSearch] = useState("");
  const [poolOpen, setPoolOpen] = useState(false);

  // Floating action button state
  const [fabOpen, setFabOpen] = useState(false);

  // Custom dialog state
  const [customSectionDialogOpen, setCustomSectionDialogOpen] = useState(false);
  const [customStructureDialogOpen, setCustomStructureDialogOpen] = useState(false);
  const [customStructureSectionToken, setCustomStructureSectionToken] = useState<string | undefined>();

  // Inline editing state for titles
  const [editingTitle, setEditingTitle] = useState<string | null>(null);
  const [titleDraft, setTitleDraft] = useState("");

  // Default text editing state
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const [textDrafts, setTextDrafts] = useState<Record<string, string>>({});
  const [textDirty, setTextDirty] = useState<Record<string, boolean>>({});

  // Sort sections by order_index
  const sortedSections = useMemo(
    () => [...sections].sort((a, b) => a.order_index - b.order_index),
    [sections]
  );

  // Filter pool sections by search
  const filteredPool = useMemo(() => {
    // Pool sections that aren't already in the registry
    const existingTokens = new Set(sections.map((s) => s.section_token));
    const available = availablePoolSections.filter(
      (s) => !existingTokens.has(s.section_token)
    );
    if (!poolSearch) return available;
    const search = poolSearch.toLowerCase();
    return available.filter(
      (s) =>
        s.section_title.toLowerCase().includes(search) ||
        s.section_token.toLowerCase().includes(search)
    );
  }, [availablePoolSections, sections, poolSearch]);

  // Get available standard sections (not already in template)
  const availableStandardSections = useMemo(() => {
    const existingTokens = new Set(sections.map((s) => s.section_token.toUpperCase()));
    return STANDARD_SECTIONS.filter((s) => !existingTokens.has(s.token));
  }, [sections]);

  // Handle quick-add of a standard section
  const handleQuickAddSection = async (token: string, title: string) => {
    if (!onAddFromPool) return;
    // Create a CombinedSectionItem for the standard section
    const sectionItem: CombinedSectionItem = {
      section_token: token,
      section_title: title,
      order_index: sections.length, // Add at end
      source: "modality" as const,
    };
    await onAddFromPool(sectionItem);
    setFabOpen(false);
  };

  // Handlers
  const handleStartEditTitle = (token: string, currentTitle: string) => {
    if (!isAdmin) return;
    setEditingTitle(token);
    setTitleDraft(currentTitle);
  };

  const handleSaveTitle = async (token: string) => {
    if (!onUpdateSectionTitle) return;
    const success = await onUpdateSectionTitle(token, titleDraft);
    if (success) setEditingTitle(null);
  };

  const handleCancelEditTitle = () => {
    setEditingTitle(null);
    setTitleDraft("");
  };

  const handleToggleExpand = (token: string, defaultText: string) => {
    if (expandedSection === token) {
      setExpandedSection(null);
    } else {
      setExpandedSection(token);
      if (!(token in textDrafts)) {
        setTextDrafts((prev) => ({ ...prev, [token]: defaultText }));
      }
    }
  };

  const handleSaveDefaultText = async (token: string) => {
    if (!onUpdateDefaultText) return;
    const success = await onUpdateDefaultText(token, textDrafts[token] || "");
    if (success) {
      setTextDirty((prev) => ({ ...prev, [token]: false }));
    }
  };

  const handleResetDefaultText = (token: string, originalText: string) => {
    setTextDrafts((prev) => ({ ...prev, [token]: originalText }));
    setTextDirty((prev) => ({ ...prev, [token]: false }));
  };

  const handleMoveUp = async (index: number) => {
    if (index === 0 || !onReorderSections) return;
    const tokens = sortedSections.map((s) => s.section_token);
    [tokens[index - 1], tokens[index]] = [tokens[index], tokens[index - 1]];
    await onReorderSections(tokens);
  };

  const handleMoveDown = async (index: number) => {
    if (index >= sortedSections.length - 1 || !onReorderSections) return;
    const tokens = sortedSections.map((s) => s.section_token);
    [tokens[index], tokens[index + 1]] = [tokens[index + 1], tokens[index]];
    await onReorderSections(tokens);
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Stethoscope className="h-5 w-5 text-primary" />
            <CardTitle className="text-lg">Sections</CardTitle>
            {sections.length > 0 && (
              <Badge variant="secondary">{sections.length}</Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            {isCustomized && onRestoreDefaults && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onRestoreDefaults}
                disabled={updating}
                className="text-muted-foreground hover:text-foreground"
              >
                <RotateCcw className="h-4 w-4 mr-1" />
                Restore Defaults
              </Button>
            )}
            {isAdmin && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={updating || poolLoading}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Section
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setPoolOpen(!poolOpen)}>
                    <Search className="h-4 w-4 mr-2" />
                    From pool
                  </DropdownMenuItem>
                  {onCreateCustomSection && (
                    <DropdownMenuItem onClick={() => setCustomSectionDialogOpen(true)}>
                      <Sparkles className="h-4 w-4 mr-2" />
                      Create custom section
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
        <CardDescription>
          {isAdmin
            ? "Manage report sections: reorder, rename, edit defaults, or toggle visibility"
            : "Configure which sections appear in your reports"}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {poolLoading ? (
          <div className="flex items-center justify-center py-4">
            <Skeleton className="h-8 w-48" />
          </div>
        ) : (
          <>
            {/* Section rows */}
            {sortedSections.map((section, index) => {
              const isEnabled = enabledTokens.has(section.section_token);
              const isEditingThisTitle = editingTitle === section.section_token;
              const isExpanded = expandedSection === section.section_token;

              return (
                <div
                  key={section.section_token}
                  className={`border rounded-lg p-3 space-y-2 transition-colors ${
                    !isEnabled ? "opacity-50 bg-muted/30" : "bg-background"
                  }`}
                >
                  {/* Row header */}
                  <div className="flex items-center gap-2">
                    {/* Reorder arrows — admin only */}
                    {isAdmin && onReorderSections && (
                      <div className="flex flex-col gap-0.5">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-5 w-5"
                          disabled={index === 0 || updating}
                          onClick={() => handleMoveUp(index)}
                        >
                          <ChevronUp className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-5 w-5"
                          disabled={
                            index >= sortedSections.length - 1 || updating
                          }
                          onClick={() => handleMoveDown(index)}
                        >
                          <ChevronDown className="h-3 w-3" />
                        </Button>
                      </div>
                    )}

                    {/* Active toggle */}
                    {onToggleEnabled && (
                      <Switch
                        checked={isEnabled}
                        onCheckedChange={(checked) =>
                          onToggleEnabled(section.section_token, checked)
                        }
                        disabled={updating}
                      />
                    )}

                    {/* Section title — inline editable in admin mode */}
                    <div className="flex-1 min-w-0">
                      {isEditingThisTitle ? (
                        <div className="flex items-center gap-1">
                          <Input
                            value={titleDraft}
                            onChange={(e) => setTitleDraft(e.target.value)}
                            className="h-7 text-sm"
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === "Enter")
                                handleSaveTitle(section.section_token);
                              if (e.key === "Escape") handleCancelEditTitle();
                            }}
                          />
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 px-2"
                            onClick={() =>
                              handleSaveTitle(section.section_token)
                            }
                            disabled={updating}
                          >
                            <Save className="h-3 w-3" />
                          </Button>
                        </div>
                      ) : (
                        <span
                          className={`font-medium text-sm ${
                            isAdmin
                              ? "cursor-pointer hover:underline"
                              : ""
                          }`}
                          onClick={() =>
                            handleStartEditTitle(
                              section.section_token,
                              section.section_title
                            )
                          }
                        >
                          {section.section_title}
                        </span>
                      )}
                    </div>

                    {/* Token badge */}
                    <Badge variant="outline" className="text-xs font-mono shrink-0">
                      {section.section_token}
                    </Badge>

                    {/* Expand default text toggle */}
                    {isAdmin && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 shrink-0"
                        onClick={() =>
                          handleToggleExpand(
                            section.section_token,
                            section.default_text
                          )
                        }
                      >
                        <ChevronRight
                          className={`h-4 w-4 transition-transform ${
                            isExpanded ? "rotate-90" : ""
                          }`}
                        />
                      </Button>
                    )}

                    {/* Delete button — admin only */}
                    {isAdmin && onDeleteSection && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-destructive hover:text-destructive shrink-0"
                        onClick={() =>
                          onDeleteSection(section.section_token)
                        }
                        disabled={updating}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>

                  {/* Collapsible expanded content — admin only */}
                  {isAdmin && isExpanded && (
                    <div className="pl-8">
                      {hasStructureSupport ? (
                        <Tabs defaultValue="text" className="w-full">
                          <TabsList className="h-8 mb-3">
                            <TabsTrigger value="text" className="text-xs h-7 px-3">
                              <FileText className="h-3 w-3 mr-1.5" />
                              Default Text
                            </TabsTrigger>
                            <TabsTrigger value="structures" className="text-xs h-7 px-3">
                              <Box className="h-3 w-3 mr-1.5" />
                              Structures
                              {assignedStructures.filter(s => s.section_token === section.section_token).length > 0 && (
                                <Badge variant="secondary" className="ml-1.5 h-4 px-1 text-[10px]">
                                  {assignedStructures.filter(s => s.section_token === section.section_token).length}
                                </Badge>
                              )}
                            </TabsTrigger>
                          </TabsList>
                          <TabsContent value="text" className="mt-0 space-y-2">
                            <Textarea
                              value={textDrafts[section.section_token] ?? section.default_text}
                              onChange={(e) => {
                                setTextDrafts((prev) => ({
                                  ...prev,
                                  [section.section_token]: e.target.value,
                                }));
                                setTextDirty((prev) => ({
                                  ...prev,
                                  [section.section_token]: true,
                                }));
                              }}
                              placeholder={`Enter default ${section.section_title.toLowerCase()} text...`}
                              className="min-h-[80px] font-mono text-sm"
                            />
                            {textDirty[section.section_token] && (
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() =>
                                    handleResetDefaultText(
                                      section.section_token,
                                      section.default_text
                                    )
                                  }
                                  disabled={updating}
                                >
                                  <RotateCcw className="h-4 w-4 mr-1" />
                                  Reset
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={() =>
                                    handleSaveDefaultText(section.section_token)
                                  }
                                  disabled={updating}
                                >
                                  <Save className="h-4 w-4 mr-1" />
                                  Save
                                </Button>
                              </div>
                            )}
                          </TabsContent>
                          <TabsContent value="structures" className="mt-0">
                            <StructurePanel
                              sectionToken={section.section_token}
                              assignedStructures={assignedStructures}
                              availableStructures={availableStructures}
                              searchResults={structureSearchResults}
                              loading={structuresLoading}
                              searchLoading={structureSearchLoading}
                              updating={updating}
                              onToggleStructure={(structureKey, include) =>
                                onToggleStructure!(section.section_token, structureKey, include)
                              }
                              onSearch={onStructureSearch || (() => {})}
                              onClearSearch={onClearStructureSearch || (() => {})}
                              onOpenCreateStructure={
                                onCreateCustomStructure
                                  ? () => {
                                      setCustomStructureSectionToken(section.section_token);
                                      setCustomStructureDialogOpen(true);
                                    }
                                  : undefined
                              }
                            />
                          </TabsContent>
                        </Tabs>
                      ) : (
                        <div className="space-y-2">
                          <Label className="text-xs text-muted-foreground">
                            Default Text
                          </Label>
                          <Textarea
                            value={textDrafts[section.section_token] ?? section.default_text}
                            onChange={(e) => {
                              setTextDrafts((prev) => ({
                                ...prev,
                                [section.section_token]: e.target.value,
                              }));
                              setTextDirty((prev) => ({
                                ...prev,
                                [section.section_token]: true,
                              }));
                            }}
                            placeholder={`Enter default ${section.section_title.toLowerCase()} text...`}
                            className="min-h-[80px] font-mono text-sm"
                          />
                          {textDirty[section.section_token] && (
                            <div className="flex items-center gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() =>
                                  handleResetDefaultText(
                                    section.section_token,
                                    section.default_text
                                  )
                                }
                                disabled={updating}
                              >
                                <RotateCcw className="h-4 w-4 mr-1" />
                                Reset
                              </Button>
                              <Button
                                size="sm"
                                onClick={() =>
                                  handleSaveDefaultText(section.section_token)
                                }
                                disabled={updating}
                              >
                                <Save className="h-4 w-4 mr-1" />
                                Save
                              </Button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}

            {sections.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">
                No sections configured for this template
              </p>
            )}

            {/* Floating Action Button for quick-add standard sections */}
            {isAdmin && onAddFromPool && availableStandardSections.length > 0 && (
              <div className="fixed bottom-6 right-6 z-50">
                {/* Expanded menu */}
                {fabOpen && (
                  <div className="absolute bottom-16 right-0 bg-card border rounded-lg shadow-lg p-2 min-w-[200px] animate-in fade-in slide-in-from-bottom-2 duration-200">
                    <div className="flex items-center justify-between px-2 py-1.5 mb-1 border-b">
                      <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                        Quick Add Section
                      </span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-5 w-5"
                        onClick={() => setFabOpen(false)}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                    <div className="space-y-0.5">
                      {availableStandardSections.map((section) => {
                        const Icon = section.icon;
                        return (
                          <button
                            key={section.token}
                            onClick={() => handleQuickAddSection(section.token, section.title)}
                            disabled={updating}
                            className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm hover:bg-accent transition-colors text-left disabled:opacity-50"
                          >
                            <Icon className="h-4 w-4 text-muted-foreground" />
                            <span>{section.title}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* FAB button */}
                <Button
                  size="icon"
                  className={`h-12 w-12 rounded-full shadow-lg transition-transform ${
                    fabOpen ? "rotate-45 bg-muted hover:bg-muted" : ""
                  }`}
                  onClick={() => setFabOpen(!fabOpen)}
                  disabled={updating}
                >
                  <Plus className="h-5 w-5" />
                </Button>
              </div>
            )}

            {/* Add from Pool — shown when poolOpen is true */}
            {isAdmin && poolOpen && (
              <div className="border rounded-lg p-3 space-y-3 bg-muted/20">
                <Label className="text-xs text-muted-foreground">
                  Search all sections
                </Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search sections..."
                    value={poolSearch}
                    onChange={(e) => setPoolSearch(e.target.value)}
                    className="pl-9 h-9"
                    autoFocus
                  />
                </div>
                <div className="space-y-1 max-h-64 overflow-y-auto">
                  {filteredPool.map((section) => (
                    <div
                      key={section.section_token}
                      className="flex items-center justify-between p-2 rounded-md hover:bg-primary/5 cursor-pointer transition-colors group"
                      onClick={async () => {
                        if (onAddFromPool) {
                          await onAddFromPool(section);
                        }
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <Plus className="h-4 w-4 text-muted-foreground group-hover:text-primary" />
                        <span className="text-sm font-medium">
                          {section.section_title}
                        </span>
                      </div>
                      <Badge
                        variant="secondary"
                        className={`text-[10px] px-1.5 py-0.5 ${
                          section.source === "body_region"
                            ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
                            : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400"
                        }`}
                      >
                        {section.source === "body_region" ? (
                          <span className="flex items-center gap-0.5">
                            <MapPin className="h-2.5 w-2.5" />
                            Body Region
                          </span>
                        ) : (
                          <span className="flex items-center gap-0.5">
                            <Layers className="h-2.5 w-2.5" />
                            General
                          </span>
                        )}
                      </Badge>
                    </div>
                  ))}
                  {filteredPool.length === 0 && (
                    <p className="text-xs text-muted-foreground py-4 text-center">
                      {poolSearch
                        ? "No sections match your search"
                        : "No additional sections available"}
                    </p>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>

      {/* Custom Section Dialog */}
      {onCreateCustomSection && (
        <CreateCustomSectionDialog
          open={customSectionDialogOpen}
          onOpenChange={setCustomSectionDialogOpen}
          onCreate={onCreateCustomSection}
          examFamilyId={examFamilyId}
        />
      )}

      {/* Custom Structure Dialog */}
      {onCreateCustomStructure && (
        <CreateCustomStructureDialog
          open={customStructureDialogOpen}
          onOpenChange={setCustomStructureDialogOpen}
          onCreate={onCreateCustomStructure}
          sectionToken={customStructureSectionToken}
          sectionOptions={sections.map((s) => ({
            token: s.section_token,
            title: s.section_title,
          }))}
        />
      )}
    </Card>
  );
}
